import React from 'react';
import { Device } from '../../lib/types';
import { findFosterHomeForDevice, type FosterHome } from '../../lib/fosterAdapters';
import { isFosterContactComplete, formatDate, formatDaysAgo } from '../../lib/utils';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import { HoverTip } from '../../components/InfoTip/InfoTip';
import { formatScheduledFor } from '../../lib/scheduling';
import Button from '../../components/Button/Button';
import ActionMenu, { type ActionMenuEntry } from '../../components/ActionMenu/ActionMenu';
import DeviceCell from './DeviceCell';
import DeviceStatusCell from './DeviceStatusCell';
import PetNameCell from './PetNameCell';
import { SETUP_STATUS_TOOLTIP } from './setupStatus';
import styles from './Devices.module.scss';

interface DeviceRowProps {
  device: Device;
  onAddPet: (device: Device) => void;
  onTransferFoster: (device: Device) => void;
  onRecall: (device: Device) => void;
  onTransferAdopter: (device: Device) => void;
  /** One-click adopt-out using the foster household's info (Fosters table only). */
  onAdoptFromFoster?: (device: Device) => void;
  /** Opens the scheduled hand-off booked for this pet, to change its date and time. */
  onEditScheduled?: (device: Device) => void;
  /** Opens the confirmation for calling off this pet's booked hand-off. */
  onCancelScheduled?: (device: Device) => void;
  /** Opens the saved foster home behind this placement (Fosters table only). */
  onViewFoster?: (home: FosterHome) => void;
  /** The rescue's saved homes, used to resolve the foster behind a placement for onViewFoster. */
  fosterHomes?: FosterHome[];
  onSendSms: (device: Device) => void;
  /** Show the Holder cell (kept in sync with the table's Holder column). */
  showHolder?: boolean;
  /** Show the Placed On cell (kept in sync with the table's Placed On column). */
  showPlacedOn?: boolean;
}

export default function DeviceRow({
  device,
  onAddPet,
  onTransferFoster,
  onRecall,
  onTransferAdopter,
  onAdoptFromFoster,
  onEditScheduled,
  onCancelScheduled,
  onViewFoster,
  fosterHomes,
  onSendSms,
  showHolder = true,
  showPlacedOn = false,
}: DeviceRowProps) {
  const isOnline = device.status === 'online';
  // Resolved on the row itself, so it is right the moment the row renders — no optimistic
  // gate while a separate lookup catches up, and nothing to be wrong about for a pet whose
  // adoption never involved a device.
  //
  // `adoptionState` is undefined on a row from the drained `rescueDevicesQuery`, which does not
  // select it. Every device this component renders comes from the paged document, so it is
  // always fetched here — but the comparison is written to read false rather than true if that
  // ever stops holding, since this gate only hides destructive actions.
  const petAdopted = device.adoptionState === 'COMPLETED';

  // Setup pills
  const szVariant = device.safeZone ? 'success' : 'error';
  const wifiVariant = device.wifiConfigured ? 'success' : 'error';

  // Actions depend on holder type and dog assignment. Anything beyond a single action goes
  // into a menu rather than a row of buttons — the Action column was the widest on the table
  // and the least informative, and these tables need the width for their data columns.
  const menuLabel = device.petName ? `Actions for ${device.petName}` : `Actions for device ${device.id}`;
  // A pet with a hand-off already booked can't start another; the badge says why the menu is greyed.
  // A pet can have at most one booking, and it blocks both hand-offs. The reason stays terse because
  // the row badge names which hand-off is booked and the Edit entry carries its date and time.
  const scheduled = device.scheduledHandoff;
  const scheduledFor = scheduled?.kind === 'FOSTER' ? 'foster' : 'adoption';
  const scheduledTitle = scheduled ? 'Already scheduled' : undefined;
  const scheduledWhen = scheduled
    ? formatScheduledFor(scheduled.scheduledFor, scheduled.scheduledForTimezone)
    : undefined;
  const scheduledEntries: ActionMenuEntry[] = scheduled
    ? [
        !!onEditScheduled && {
          label: `Edit scheduled ${scheduledFor}`,
          onSelect: () => onEditScheduled(device),
        },
        !!onCancelScheduled && {
          label: `Cancel scheduled ${scheduledFor}`,
          onSelect: () => onCancelScheduled(device),
          danger: true,
        },
      ]
    : [];
  let actions: React.ReactNode;
  if (device.holderType === 'rescue' && !device.petName) {
    // A lone action stays a plain button; a menu wrapping one item is a click for nothing.
    actions = (
      <Button variant="green" size="sm" onClick={() => onAddPet(device)}>
        Add Pet
      </Button>
    );
  } else if (device.holderType === 'foster') {
    // Adopt-out to the foster household in one click. Disabled when the foster's
    // contact info is incomplete, since the transfer sends the adopter an email.
    const canAdoptFromFoster = isFosterContactComplete(device);
    // Only placements made with `saveToRoster` have a home to show, so the entry is omitted
    // rather than disabled when the lookup comes up empty.
    const fosterHome = onViewFoster && fosterHomes ? findFosterHomeForDevice(device, fosterHomes) : null;
    const items: ActionMenuEntry[] = [
      { label: 'Send SMS', onSelect: () => onSendSms(device) },
      !!fosterHome && { label: 'View Foster', onSelect: () => onViewFoster?.(fosterHome) },
      ...scheduledEntries,
      {
        label: 'Adopt (Foster Fail)',
        onSelect: () => onAdoptFromFoster?.(device),
        disabled: !canAdoptFromFoster || !!scheduled,
        title: scheduledTitle ?? (canAdoptFromFoster ? undefined : "Add this foster's email and phone number first"),
      },
      // All three take the pet back from its foster, so the heading carries the verb and each entry
      // names only where the pet goes next. Distinct from the foster-fail entry above, which adopts
      // the pet to the home it is already in and so recalls nothing.
      {
        sectionBefore: 'Recall',
        label: 'For External Adoption',
        ariaLabel: 'Recall for external adoption',
        onSelect: () => onTransferAdopter(device),
        disabled: !!scheduled,
        title: scheduledTitle,
      },
      {
        label: 'For Foster Transfer',
        ariaLabel: 'Recall for foster transfer',
        onSelect: () => onTransferFoster(device),
        disabled: !!scheduled,
        title: scheduledTitle,
      },
      { label: 'Back To Rescue', ariaLabel: 'Recall back to rescue', onSelect: () => onRecall(device) },
    ];
    actions = <ActionMenu items={items} ariaLabel={menuLabel} />;
  } else if (petAdopted) {
    actions = <span className={styles.textMuted}>&mdash;</span>;
  } else {
    const items: ActionMenuEntry[] = [
      ...scheduledEntries,
      { label: 'Adopt', onSelect: () => onTransferAdopter(device), disabled: !!scheduled, title: scheduledTitle },
      { label: 'Foster', onSelect: () => onTransferFoster(device), disabled: !!scheduled, title: scheduledTitle },
      { label: 'Unlink Device', onSelect: () => onRecall(device), danger: true, dividerBefore: true },
    ];
    actions = <ActionMenu items={items} ariaLabel={menuLabel} />;
  }

  return (
    <tr>
      {showHolder && (
        <td>
          <span className={styles.holderName}>{device.holderName}</span>
        </td>
      )}
      <td>
        {device.petName ? (
          <PetNameCell name={device.petName} photoThumbUrl={device.petPhotoThumbUrl} />
        ) : (
          <span className={styles.textMuted}>Unassigned</span>
        )}
        {scheduled && (
          <div className={styles.scheduledBadge} title={scheduledWhen}>
            <StatusBadge variant="info">
              {scheduled.kind === 'FOSTER' ? 'Scheduled for Foster' : 'Scheduled for Adoption'}
            </StatusBadge>
          </div>
        )}
      </td>
      <td>
        <DeviceCell moduleId={device.id} type={device.type} showBattery battery={device.battery} />
      </td>
      {showPlacedOn && (
        <td className={styles.nowrap}>
          {/* `transferDate` on an IN_FOSTER row is the placement date — see the Device type. */}
          <div className={styles.cellMain}>{formatDate(device.transferDate)}</div>
          {device.transferDate && <div className={styles.cellSub}>{formatDaysAgo(device.transferDate)}</div>}
        </td>
      )}
      <td>
        {/* The HoverTip span replaces the pills' own wrapper rather than nesting inside it, so
            the flex column is unchanged and hovering either badge — or the gap between them —
            explains what their colour means. */}
        <HoverTip className={styles.setupPills} tip={SETUP_STATUS_TOOLTIP}>
          <StatusBadge variant={szVariant}>Safe Zone</StatusBadge>
          <StatusBadge variant={wifiVariant}>WiFi</StatusBadge>
        </HoverTip>
      </td>
      <td>
        <DeviceStatusCell isOnline={isOnline} lastSeen={device.lastSeen} />
      </td>
      <td>{actions}</td>
    </tr>
  );
}
